import React, { createContext, useContext, useEffect, useState } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import type { Profile, UserRole } from '../types/database';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  roles: UserRole[];
  loading: boolean;
  isStaff: boolean;
  isAdmin: boolean;
  isInstructor: boolean;
  isClient: boolean;
  isStudent: boolean;
  hasRole: (role: UserRole) => boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: any; data: any }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const STAFF_ROLES: UserRole[] = [
  'super_admin',
  'admin',
  'operations_manager',
  'sales_manager',
  'project_manager',
  'finance_officer',
  'support_agent',
];

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchUserData = async (userId: string) => {
    try {
      // 1. Fetch Profile
      const { data: profileData, error: profileErr } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (profileErr && profileErr.code !== 'PGRST116') {
        // PGRST116 = row not found; that's acceptable (new user)
        console.error('[AUTH] Profile fetch error:', profileErr);
      }

      if (profileData) {
        setProfile(profileData as Profile);
      } else {
        setProfile(null);
      }

      // 2. Fetch Roles
      const { data: rolesData, error: rolesErr } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);

      if (rolesErr) {
        console.error('[AUTH] Roles fetch error:', rolesErr);
      }

      if (rolesData && rolesData.length > 0) {
        setRoles(rolesData.map(r => r.role as UserRole));
      } else {
        // Default role fallback for client/student
        setRoles(['client', 'student']);
      }
    } catch (err) {
      console.error('[AUTH] Error fetching user profile/roles:', err);
      // Even on error, set defaults so the app doesn't freeze
      setProfile(null);
      setRoles(['client', 'student']);
    }
  };

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();

        if (error) {
          console.error('[AUTH] getSession error:', error);
        }

        if (mounted) {
          const session = data?.session ?? null;
          setSession(session);
          setUser(session?.user ?? null);

          if (session?.user) {
            try {
              await fetchUserData(session.user.id);
            } catch (fetchErr) {
              console.error('[AUTH] fetchUserData error during init:', fetchErr);
            }
          }
        }
      } catch (err) {
        console.error('[AUTH] Unexpected initialization error:', err);
      } finally {
        if (mounted) {
          setLoading(false);
          console.log('[AUTH] Initialization completed');
        }
      }
    };

    initializeAuth();

    // Listen for auth state changes (fires after initial session is already handled)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, newSession) => {
      if (!mounted) return;

      setSession(newSession);
      setUser(newSession?.user ?? null);

      if (newSession?.user) {
        try {
          await fetchUserData(newSession.user.id);
        } catch (fetchErr) {
          console.error('[AUTH] fetchUserData error on state change:', fetchErr);
        }
      } else {
        setProfile(null);
        setRoles([]);
      }

      // Always ensure loading ends after auth state changes
      if (mounted) {
        setLoading(false);
      }
    });

    // Safety net: if for any reason loading never resolves within 10s, force-complete
    const safetyTimer = setTimeout(() => {
      if (mounted) {
        console.warn('[AUTH] Safety timeout triggered - forcing loading to false');
        setLoading(false);
      }
    }, 10000);

    return () => {
      mounted = false;
      clearTimeout(safetyTimer);
      subscription.unsubscribe();
    };
  }, []);

  const refreshProfile = async () => {
    if (user) {
      await fetchUserData(user.id);
    }
  };

  const signIn = async (email: string, password: string) => {
    const res = await supabase.auth.signInWithPassword({ email, password });
    if (res.data?.user) {
      setUser(res.data.user);
      await fetchUserData(res.data.user.id);
    }
    return { error: res.error };
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    const res = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
        },
      },
    });

    if (res.data?.user) {
      setUser(res.data.user);
      // Wait for trigger to create profile
      setTimeout(() => {
        fetchUserData(res.data.user!.id);
      }, 500);
    }

    return { error: res.error, data: res.data };
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch (err) {
      console.warn('Supabase signOut warning:', err);
    } finally {
      setUser(null);
      setSession(null);
      setProfile(null);
      setRoles([]);

      // Clear any cached credentials or app items in storage safely
      try {
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (key.startsWith('sb-') || key.includes('supabase') || key.includes('biento_auth'))) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach((k) => localStorage.removeItem(k));
        sessionStorage.clear();
      } catch (storageErr) {
        console.warn('Error clearing browser storage:', storageErr);
      }
    }
  };

  const hasRole = (role: UserRole) => roles.includes(role);
  const isStaff = roles.some(r => STAFF_ROLES.includes(r));
  const isAdmin = roles.includes('super_admin') || roles.includes('admin');
  const isInstructor = roles.includes('instructor') || isAdmin;
  const isClient = roles.includes('client');
  const isStudent = roles.includes('student');

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        roles,
        loading,
        isStaff,
        isAdmin,
        isInstructor,
        isClient,
        isStudent,
        hasRole,
        signIn,
        signUp,
        signOut,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
